import React, { useEffect, useState } from "react";
import { Alert, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useGeneralApiCall } from "@/services/useGeneralApiCall";
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  DragOverlay,
  useSensor,
  useSensors,
  UniqueIdentifier,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy ,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { SortableImageProps, DragDrapImagesProps } from "@/types";
import Breadcrumb from "./Breadcrumb";
import DummyImage from "../../public/images/dummy_image.jpg";
import Chip from "@mui/material/Chip";
import "../styles/StyleOrderUpdatedComponent.css";

const SortableImage: React.FC<SortableImageProps> = ({
  image,
  navigate,
  path,
  gridCol,
}) => {

  const { attributes, listeners, setNodeRef, transform } = useSortable({
    id: image?.style_id,
    animateLayoutChanges: ()=> false,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transform ? "transform 120ms ease" : "none",
  };

  const fontSize =
    gridCol === undefined
      ? undefined
      : gridCol >= 15
        ? 11
        : gridCol >= 10
          ? 12
          : gridCol >= 5
            ? 14
            : undefined;

  const lineClamp =
    gridCol === undefined
      ? undefined
      : gridCol >= 10
        ? 1
        : gridCol >= 5
          ? 2
          : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="preview-item"
      onClick={() => navigate(`${path}/${image?.style_id}`)}
      {...attributes}
      {...listeners}
    >
      <img
        src={image?.cover_image || image?.photos?.[0]?.url || DummyImage}
        alt={`Preview of ${image.category}`}
        className="preview-image"
      />
      <div className="image-info">
        <p
          title={image.name}
          style={{
            fontSize,
            lineHeight: fontSize ? "1.3" : undefined,
            display: lineClamp ? "-webkit-box" : undefined,
            WebkitLineClamp: lineClamp,
            WebkitBoxOrient: lineClamp ? "vertical" : undefined,
            overflow: lineClamp ? "hidden" : undefined,
            textAlign: "center",
            margin: 0,
          }}
        >
          {image.name}
        </p>
      </div>
    </div>
  );
};

const StyleOrderUpdatedComponent: React.FC<DragDrapImagesProps> = ({
  initialImagesTemp,
  path,
  title,
  categories,
  setCategories,
  handlePostApi,
  isLoading,
}) => {
  const { getApi } = useGeneralApiCall();
  const initialImages = initialImagesTemp || [];
  const [images, setImages] = useState<any[]>(initialImages);
  const [isDragEnabled, setIsDragEnabled] = useState<boolean>(false);
  const [parentCategories, setParentCategories] = useState<any[]>([]);
  const [gridCol, setGridCol] = useState<number>();
  const [activeImage, setActiveImage] = useState<any | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    setImages(initialImages);
  }, [initialImages]);

  const fetchCategories = async () => {
    try {
      const data = await getApi("/api/cat/v1/parent-categories/");
      setParentCategories(data.data as any[]);
    } catch (error: any) {
      console.log(error);
      setParentCategories([]);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleSubmit = (images: any) => {
    if (handlePostApi) handlePostApi(images);
    setIsDragEnabled(false);
  };

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 3 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 100, tolerance: 5 },
    }),
  );

  const handleDragEnd = ({
    active,
    over,
  }: {
    active: { id: UniqueIdentifier };
    over: { id: UniqueIdentifier } | null;
  }) => {
    if (over && active.id !== over.id) {
      const img1OldIndex = images.findIndex(
        (img) => String(img?.style_id) === active.id,
      );
      const img1NewIndex = images.findIndex(
        (img) => String(img?.style_id) === over.id,
      );

      setImages((prev) => {
        const reordered = arrayMove(prev, img1OldIndex, img1NewIndex);
        return reordered.map((img, idx) => ({ ...img, index: idx }));
      });
    }
    setActiveImage(null);
  };

  useEffect(() => {
    if (!isDragEnabled) {
      setImages(initialImages);
    }
  }, [isDragEnabled]);

  return (
    <>
      <div className="d-flex gap-2 justify-content-between  mb-4">
        <Breadcrumb />
        <div className="d-flex gap-2">
          <Button
            style={{ fontSize: "22px" }}
            onClick={() => navigate("/add-new-style")}
            variant={"success"}
          >
            Create new style
          </Button>
          {categories && (
            <>
              <Button
                style={{ fontSize: "22px" }}
                onClick={() => setIsDragEnabled((prev) => !prev)}
                variant={isDragEnabled ? "danger" : "danger"}
              >
                {isDragEnabled ? "Disable Drag & Drop" : "Enable Drag & Drop"}
              </Button>
              {isDragEnabled && (
                <Button
                  onClick={() => handleSubmit(images)}
                  disabled={isLoading}
                >
                  {isLoading ? "Submitting..." : "Submit"}
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {title && <Alert className="text-center">{title}</Alert>}

      <div style={{ marginBottom: 16 }}>
        {parentCategories?.map((cat: any) => {
          const href = `/style-management/${cat.slug}`;
          return (
            <a
              key={cat.id || cat.name}
              href={href}
              style={{ textDecoration: "none", display: "inline-block" }}
              onClick={(e) => {
                if (
                  e.button === 0 &&
                  !e.metaKey &&
                  !e.ctrlKey &&
                  !e.altKey &&
                  !e.shiftKey
                ) {
                  e.preventDefault();
                  if (typeof setCategories === "function")
                    setCategories(cat.slug);
                  navigate(`/style-management/${cat.slug}`);
                }
              }}
            >
              <Chip
                label={cat.name}
                style={{
                  marginRight: 8,
                  marginBottom: 8,
                  fontSize: 18,
                  padding: "20px 20px",
                  height: 60,
                }}
                color={categories === cat.slug ? "primary" : "default"}
              />
            </a>
          );
        })}
      </div>

      {isDragEnabled && (
        <div
          className="flex gap-3 justify-content-end"
          style={{ marginBottom: "16px", color: "gray" }}
        >
          {[3, 5, 10, 15].map((col) => (
            <Button
              key={col}
              className={`btn-${gridCol === col ? "danger" : "primary"}`}
              onClick={() => setGridCol(col)}
            >
              {col}
            </Button>
          ))}
        </div>
      )}

      {isDragEnabled ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={(event) => {
            const img = images.find(
              (i) => String(i.style_id) === String(event.active.id),
            );
            setActiveImage(img || null);
          }}
          onDragEnd={handleDragEnd}
          onDragCancel={() => setActiveImage(null)}
          
        >
          <SortableContext
            items={images.map((img) => img?.style_id)}
            strategy={rectSortingStrategy }
          >
            <div
              className={`preview-grid ${
                isDragEnabled && gridCol && gridCol >= 10 ? "dense-grid" : ""
              }`}
              style={{ gridTemplateColumns: `repeat(${gridCol || 3}, 1fr)` }}
            >
              {images.map((image) => (
                <SortableImage
                  key={image?.style_id}
                  image={image}
                  navigate={navigate}
                  path={path}
                  gridCol={gridCol}
                />
              ))}
            </div>
          </SortableContext>

          <DragOverlay adjustScale={false}>
            {activeImage ? (
              <div className="preview-item">
                <img
                  src={
                    activeImage.cover_image ||
                    activeImage.photos?.[0]?.url ||
                    DummyImage
                  }
                  className="preview-image"
                  alt=""
                />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      ) : (
        <div
          className={`preview-grid ${
            isDragEnabled && gridCol && gridCol >= 10 ? "dense-grid" : ""
          }`}
        >
          {images?.map((image) => (
            <a
              key={image.style_id}
              className="preview-item"
              href={`${path}/${image?.style_id}`}
              onClick={(e) => {
                if (
                  e.button === 0 &&
                  !e.metaKey &&
                  !e.ctrlKey &&
                  !e.altKey &&
                  !e.shiftKey
                ) {
                  e.preventDefault();
                  navigate(`/style/details/${image?.style_id}`);
                }
              }}
              style={{
                textDecoration: "none",
                color: "inherit",
                cursor: "pointer",
              }}
            >
              <img
                src={
                  image?.cover_image || image?.photos?.[0]?.url || DummyImage
                }
                className="preview-image"
                alt={`Preview of ${image.category}`}
              />
              <div className="image-info">
                <p>{image.name}</p>
              </div>
            </a>
          ))}
        </div>
      )}
    </>
  );
};

export default StyleOrderUpdatedComponent;
